import { BUILDINGS } from '../config/buildings.js';
import { TASK_DEFINITIONS } from '../config/tasks.js';
import { CONFIG } from '../config/constants.js';

/**
 * Calculates all building bonuses for a task
 * @param {string} task - Task ID
 * @param {Array} buildings - Completed buildings array
 * @param {Object} context - Context with workerCount, rollCount, etc.
 * @returns {number} Total bonus from buildings
 */
export function calculateBuildingBonuses(task, buildings, context = {}) {
  let total = 0;

  // Find all completed buildings
  const completedBuildings = buildings.filter(b => b.complete);

  // Apply each building's effects
  completedBuildings.forEach(building => {
    const buildingDef = BUILDINGS[building.type];
    if (!buildingDef?.effects?.taskBonuses) return;

    buildingDef.effects.taskBonuses.forEach(bonus => {
      if (bonus.task !== task) return;

      // Skip immunity-type bonuses (handled separately)
      if (bonus.type === 'immunity') return;

      switch (bonus.type) {
        case 'perWorker':
          total += bonus.value * (context.workerCount || 0);
          break;
        case 'flat':
          total += bonus.value;
          break;
        case 'perRoll':
          total += bonus.value * (context.rollCount || 0);
          break;
      }
    });
  });

  return total;
}

/**
 * Checks if a task/worker combination has push injury immunity
 * @param {string} task - Task ID
 * @param {Array} buildings - Completed buildings array
 * @returns {boolean} True if immune to push injury
 */
export function hasPushInjuryImmunity(task, buildings) {
  const completedBuildings = buildings.filter(b => b.complete);

  return completedBuildings.some(building => {
    const buildingDef = BUILDINGS[building.type];
    return buildingDef?.effects?.taskBonuses?.some(
      bonus => bonus.task === task && bonus.pushInjuryImmunity === true
    );
  });
}

/**
 * Gets the special mechanic handler for a task
 * @param {string} task - Task ID
 * @param {string} phase - When to apply ('onRoll', 'onEndDay', 'onAssign')
 * @returns {string|null} Handler function name or null
 */
export function getSpecialMechanicHandler(task, phase) {
  const taskDef = TASK_DEFINITIONS[task];
  if (!taskDef?.effectHandlers) return null;

  return taskDef.effectHandlers[phase] || null;
}

/**
 * Checks if a task has a special mechanic of a given type
 * @param {string} task - Task ID
 * @param {string} mechanicType - Type to check for
 * @returns {boolean} True if task has this mechanic
 */
export function hasSpecialMechanic(task, mechanicType) {
  const taskDef = TASK_DEFINITIONS[task];
  return taskDef?.specialMechanic === mechanicType;
}

/**
 * Gets configuration for a task's special mechanic
 * @param {string} task - Task ID
 * @param {string} configKey - Config property to retrieve
 * @returns {any} Config value or undefined
 */
export function getSpecialMechanicConfig(task, configKey) {
  const taskDef = TASK_DEFINITIONS[task];
  return taskDef?.[configKey];
}

/**
 * Calculates passive resource generation from buildings
 * @param {Array} buildings - Buildings array
 * @param {Object} currentResources - Current resource values for cap checking
 * @returns {Object} Resource deltas { knowledge: 3, techPoints: 1, ... }
 */
export function calculatePassiveGeneration(buildings, currentResources = {}) {
  const deltas = {};

  buildings
    .filter(b => b.complete)
    .forEach(building => {
      const buildingDef = BUILDINGS[building.type];
      const passiveEffect = buildingDef?.effects?.passiveGeneration;

      if (passiveEffect) {
        const { resource, amount, cap } = passiveEffect;
        const currentValue = currentResources[resource] || 0;

        if (cap) {
          // Apply cap from CONFIG
          const capValue = CONFIG[cap];
          const newValue = Math.min(capValue, currentValue + amount);
          deltas[resource] = newValue - currentValue;
        } else {
          deltas[resource] = (deltas[resource] || 0) + amount;
        }
      }
    });

  return deltas;
}
